import type { PoolLedger, PoolPosition } from '@myrewards/contracts';
import { useQueries, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type RefObject,
} from 'react';
import { useLocation } from 'react-router';
import { budgetsQuery, meQuery } from '../api';
import type { HeroStat } from '../shell';
import { adjustBudget, budgetsForYearQuery, moveBudget, poolLedgerQuery } from '../api/settings';
import { opensPage } from '../app/access';
import {
  actorWords,
  Button,
  Card,
  ConfirmDialog,
  EmptyArt,
  EmptyState,
  HistoryDrawer,
  MoneyField,
  Select,
  StatusPill,
  cx,
  formatCount,
  formatDate,
  formatMoney,
  plural,
  scoped,
  useToast,
  type ConfirmSummaryRow,
  type HistoryEntry,
  type ScopedLabel,
} from '../ui';
import { fiscalYearDates, fiscalYearOn } from '../ui/fiscal';
import { LedgerCard } from './BudgetLedgerCard';
import {
  LEDGER_ANCHOR,
  POOLS_ANCHOR,
  adjustBodyOf,
  budgetWrites,
  ledgerHistoryRows,
  type BudgetValue,
  type LedgerWrite,
} from './budget-model';
import type { ForWhomValue } from '../acting-for/ForWhom';
import {
  BudgetForWhom,
  BudgetStep,
  moveChange,
  poolChange,
  useBudgetActFor,
} from './budget-acting';
import { AskDataEntry } from '../ask-data/AskDataEntry';
import { NotYourArea, SettingsFrame, type Section } from './Frame';
import { AsOf, SettingHistoryDrawer, StepChart } from './history';
import {
  LEDGER_KIND_LABEL,
  LEDGER_KIND_WORD,
  isFraction,
  ledgerSummary,
  mergeLedgers,
  organizationToday,
  percentText,
  reduce,
  signedMoney,
  stepPoints,
  type Fraction,
} from './model';
import { NumberInput } from './NumberInput';
import { canOpenArea, holds } from './nav';
import {
  ChangedLine,
  ScrollBox,
  SettingFallback,
  WhoEdits,
  jumpTo,
  problemText,
  useSetting,
  useSettingWrite,
} from './parts';
import { SettingConfirm, useSaveFor } from './SettingConfirm';
import { TaaSplitBar } from './ValuesPage';
import s from './Settings.module.css';

type Pool = PoolPosition;

const awardedCash = (p: Pool) => p.paid.cashCents + p.inProgress.cashCents;
const awardedOptions = (p: Pool) => p.paid.options + p.inProgress.options;
/**
 * APP-264 (Tom, 2026-11-01: "start with a pool that's zero"): the Spot Awards pool with no budget
 * set and awards charged to it is over budget by them. Spot Awards carry on whatever it shows.
 */
const spotOverNoBudget = (p: Pool) => p.kind === 'spot' && p.budget === null && awardedCash(p) > 0;

/** Where the hero, the setup banner and the empty ledger lead: the pools card (APP-093). */

/** "PAA/TAA", "PAA/TAA and Space Systems", "A, B and C". */
function listText(names: readonly string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1] ?? ''}`;
}

/** Refreshes every budget view after a change: positions, ledgers and the Home pool cards. */
function useBudgetRefresh(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ['budgets'] });
  };
}

function ledgerWriteText(w: LedgerWrite): string {
  const parts = [
    w.cashCents > 0 ? formatMoney(w.cashCents) : null,
    w.options > 0 ? `${formatCount(w.options)} options` : null,
  ].filter((p): p is string => p !== null);
  const sign = w.kind === 'reduction' ? '−' : '+';
  return `${LEDGER_KIND_LABEL[w.kind]} · ${w.kind === 'initial' ? '' : sign}${parts.join(' and ')}`;
}

/**
 * Over budget in words, never by colour or a minus sign alone (QA-036): "$2,913 over budget" and
 * an Over budget pill, the app's wording on Spend and Home.
 */
function OverBudget({ amount }: { amount: string }): ReactElement {
  return (
    <>
      <span className={s.down}>{`${amount} over budget`}</span>
      <span className={s.cellPill}>
        <StatusPill tone="problem" size="sm" label="Over budget" />
      </span>
    </>
  );
}

/**
 * The editor that opens under a pool's row: cash and options, then a confirm summary. The approval
 * page uses it too, to set a missing budget in place before approving (APP-070, QA-028).
 */
export function PoolEditor({
  pool,
  fiscalYear,
  onDone,
  focusOnOpen = false,
}: {
  pool: Pool;
  fiscalYear: number;
  onDone: () => void;
  /** Opened from the setup banner: focus goes straight to its first field. */
  focusOnOpen?: boolean;
}): ReactElement {
  const formRef = useRef<HTMLFormElement>(null);
  useLayoutEffect(() => {
    if (focusOnOpen) formRef.current?.querySelector('input')?.focus();
  }, [focusOnOpen]);
  const refresh = useBudgetRefresh();
  const toast = useToast();
  // APP-264: Spot Awards are paid in cash, so the Spot Awards pool's budget is cash only.
  const cashOnly = pool.kind === 'spot';
  const [cash, setCash] = useState<number | null>(pool.budget?.cashCents ?? null);
  const [options, setOptions] = useState<number | null>(
    cashOnly ? 0 : (pool.budget?.options ?? null),
  );
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const after: BudgetValue | null =
    cash === null || options === null ? null : { cashCents: cash, options };
  const writes = after === null ? [] : budgetWrites(pool.budget, after);
  const setting = pool.budget === null;
  // APP-148 D20: a Stock Admin can mark the change "For an Executive" (Budget board).
  const actFor = useBudgetActFor();
  const [who, setWho] = useState<ForWhomValue>('me');
  const acting = actFor !== null && who === 'someone';

  const summary: ConfirmSummaryRow[] = [
    {
      label: `Cash budget ${String(fiscalYear)}`,
      ...(pool.budget !== null && { before: formatMoney(pool.budget.cashCents) }),
      value: after === null ? '' : formatMoney(after.cashCents),
    },
    ...(cashOnly
      ? []
      : [
          {
            label: `Options budget ${String(fiscalYear)}`,
            ...(pool.budget !== null && { before: formatCount(pool.budget.options) }),
            value: after === null ? '' : formatCount(after.options),
          },
        ]),
    ...writes.map((w, i) => ({
      label: writes.length > 1 ? `Ledger entry ${String(i + 1)}` : 'Ledger entry',
      value: ledgerWriteText(w),
    })),
  ];

  return (
    <form
      ref={formRef}
      className={s.rowEditor}
      aria-label={`${setting ? 'Set' : 'Change'} the ${pool.pool.name} budget`}
      onSubmit={(e) => {
        e.preventDefault();
        if (writes.length > 0 && !acting) setConfirming(true);
      }}
    >
      <div className={s.fieldRow}>
        <MoneyField
          label={`${pool.pool.name} · cash budget ${String(fiscalYear)}`}
          valueCents={cash}
          onChangeCents={setCash}
          isRequired
        />
        {!cashOnly && (
          <NumberInput
            label={`${pool.pool.name} · options budget ${String(fiscalYear)}`}
            value={options}
            onChange={setOptions}
            minValue={0}
            isRequired
          />
        )}
      </div>
      <p className={s.muted} aria-live="polite">
        {writes.length === 0
          ? cashOnly
            ? setting
              ? 'Enter the cash budget for the year. Spot Awards are paid in cash, so this pool has no options budget.'
              : 'Change the cash budget to see the ledger entry it makes.'
            : setting
              ? 'Enter the cash and options budget for the year.'
              : 'Change the cash or options budget to see the ledger entry it makes.'
          : `This records ${writes.map(ledgerWriteText).join(', and ')}.`}
      </p>
      {actFor !== null && <BudgetForWhom value={who} onChange={setWho} />}
      {acting ? (
        <BudgetStep
          offer={actFor.offer}
          actorName={actFor.actorName}
          subtitle={`${pool.pool.name} · cash budget ${String(fiscalYear)}`}
          change={poolChange(
            { name: pool.pool.name, budget: pool.budget, remaining: pool.remaining },
            fiscalYear,
            after,
            writes,
          )}
          ready={writes.length > 0}
          // Back to "Just me", keeping the figures typed (as the move does).
          onCancel={() => {
            setWho('me');
          }}
          onConfirm={async (reason, actingFor, done) => {
            const body = adjustBodyOf(writes);
            if (body === null) return;
            try {
              // One request for the whole change, both entries or neither (R73).
              await adjustBudget({ poolId: pool.pool.id, fiscalYear, reason, ...body, actingFor });
            } finally {
              await refresh();
            }
            toast.show({
              tone: 'done',
              title: `Budget updated for ${done.person.name}`,
              body: `${pool.pool.name}: it’s in the budget ledger, done for ${done.person.name}.`,
            });
            onDone();
          }}
        />
      ) : (
        <div className={s.formActions}>
          <Button type="submit" variant="navy" isDisabled={writes.length === 0}>
            Review change
          </Button>
          <Button variant="ghost" onPress={onDone}>
            Cancel
          </Button>
        </div>
      )}
      <ConfirmDialog
        isOpen={confirming}
        onOpenChange={(open) => {
          setConfirming(open);
          if (!open) setError(null);
        }}
        title={
          setting ? `Set the ${pool.pool.name} budget?` : `Change the ${pool.pool.name} budget?`
        }
        intro="It goes in the fiscal-year budget ledger with your name and reason. Awards already entered keep their amounts."
        summary={summary}
        reasonLabel="Why are you making this change?"
        confirmLabel={setting ? 'Set budget' : 'Change budget'}
        error={error}
        onConfirm={async (reason) => {
          setError(null);
          const body = adjustBodyOf(writes);
          if (body === null) return false;
          try {
            // One request for the whole change, both entries or neither (R73).
            await adjustBudget({ poolId: pool.pool.id, fiscalYear, reason, ...body });
          } catch (err) {
            setError(problemText(err));
            await refresh();
            return false;
          }
          await refresh();
          toast.show({
            tone: 'done',
            title: 'Budget updated',
            body: `${pool.pool.name}: it’s in the budget ledger.`,
          });
          onDone();
          return true;
        }}
      />
    </form>
  );
}

/** A pool's History: its budget over the year from the ledger, with "as of". */
function PoolHistoryDrawer({
  ledger,
  isOpen,
  onOpenChange,
}: {
  ledger: PoolLedger | undefined;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}): ReactElement {
  const today = organizationToday();
  const [asOf, setAsOf] = useState(today);
  const rows = ledger === undefined ? [] : ledgerHistoryRows(ledger);
  const entries: HistoryEntry[] =
    ledger === undefined
      ? []
      : mergeLedgers([ledger]).map((r) => ({
          id: r.id,
          at: r.entry.at,
          actor: {
            name: actorWords(r.entry.by?.name ?? 'MyRewards setup', r.entry.previewedBy?.name),
          },
          // APP-148: "Meredith Okonkwo for Elena Brightwater".
          onBehalfOf: r.entry.for?.name ?? null,
          field: `${LEDGER_KIND_LABEL[r.entry.kind]}${
            r.entry.otherPool === null
              ? ''
              : r.entry.kind === 'move_out'
                ? ` to ${r.entry.otherPool.name}`
                : ` from ${r.entry.otherPool.name}`
          }${r.entry.voided ? ' · deleted' : ''}`,
          before: formatMoney(r.after.cashCents - (r.entry.voided ? 0 : r.entry.cashCents)),
          after: `${formatMoney(r.after.cashCents)} · ${formatCount(r.after.options)} options`,
          reason: r.entry.reason,
        }));
  // A day whose last change was done for someone says so on the chart: "top-up for Elena
  // Brightwater" (Ledger board's Value over time).
  const doneFor = new Map<string, string | null>();
  for (const e of [...(ledger?.entries ?? [])].sort((a, b) => a.at.localeCompare(b.at)))
    if (!e.voided)
      doneFor.set(
        e.at.slice(0, 10),
        e.for == null ? null : `${LEDGER_KIND_WORD[e.kind]} for ${e.for.name}`,
      );
  const points = stepPoints(rows, (v) => (typeof v === 'number' ? v : null), today).map((p) => {
    const note = doneFor.get(p.date) ?? null;
    return note === null ? p : { ...p, note };
  });
  const fmt = (v: unknown) => (typeof v === 'number' ? formatMoney(v) : 'Not set');
  const year = ledger === undefined ? null : fiscalYearDates(ledger.fiscalYear);
  return (
    <HistoryDrawer
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      subject={`History · ${ledger?.pool.name ?? 'Budget pool'} · FY${String(ledger?.fiscalYear ?? '')}`}
      title="Cash budget"
      loading={ledger === undefined}
      {...(ledger?.position.budget != null && {
        current: { label: 'Cash budget now', value: formatMoney(ledger.position.budget.cashCents) },
      })}
      entries={entries}
      overview={
        // Once the ledger is in, the chart is always there: a ghost on the year's real axis until
        // the pool's first budget (APP-093).
        ledger !== undefined && year !== null ? (
          <div className={s.overview}>
            <h3 className={cx('eyebrow', s.sectionLabel)}>Value over time</h3>
            <StepChart
              points={points}
              format={formatMoney}
              label={`${ledger.pool.name} cash budget over time`}
              today={today}
              asOf={asOf}
              empty={{
                headline: `No budget for FY${String(ledger.fiscalYear)} yet.`,
                body: 'The line starts with the first budget.',
                from: year.from,
                to: year.to,
              }}
            />
            <AsOf rows={rows} format={fmt} date={asOf} onDate={setAsOf} />
          </div>
        ) : undefined
      }
    />
  );
}

function PoolsCard({
  pools,
  ledgers,
  fiscalYear,
  years,
  onYear,
  canAdjust,
  headingRef,
  onLedger,
}: {
  pools: readonly Pool[];
  ledgers: ReadonlyMap<string, PoolLedger>;
  fiscalYear: number;
  years: readonly number[];
  onYear: (year: number) => void;
  canAdjust: boolean;
  headingRef: RefObject<HTMLHeadingElement | null>;
  /** Takes the person to the budget ledger below. */
  onLedger: () => void;
}): ReactElement {
  const [editing, setEditing] = useState<string | null>(null);
  // The pool the setup banner opened: its editor takes focus straight away.
  const [fromBanner, setFromBanner] = useState<string | null>(null);
  const [history, setHistory] = useState<string | null>(null);
  // Each pool's Edit (or Set budget) button: where focus goes back when its editor closes, after
  // a change or Cancel (QA-036, APP-046), so a keyboard user carries on from the same pool.
  const editButtons = useRef(new Map<string, HTMLButtonElement>());
  const [returnTo, setReturnTo] = useState<{ poolId: string } | null>(null);
  useEffect(() => {
    if (returnTo === null) return;
    // A frame later: after the editor and its confirmation have gone, and after React Aria has
    // tried to restore focus to their (now removed) trigger.
    const frame = requestAnimationFrame(() => {
      editButtons.current.get(returnTo.poolId)?.focus();
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [returnTo]);
  const set = pools.filter((p) => p.budget !== null);
  // Day one (X4's empty board): no pool has a budget for the year yet.
  const needSetup = pools.length > 0 && set.length === 0;
  const firstUnset = pools.find((p) => p.budget === null);
  const year = String(fiscalYear);
  const total = {
    cash: set.reduce((n, p) => n + (p.budget?.cashCents ?? 0), 0),
    adjustments: set.reduce((n, p) => {
      const l = ledgers.get(p.pool.id);
      return n + (l === undefined ? 0 : ledgerSummary(l).netCents);
    }, 0),
    awarded: pools.reduce((n, p) => n + awardedCash(p), 0),
    left: set.reduce((n, p) => n + (p.remaining?.cashCents ?? 0), 0),
    options: set.reduce((n, p) => n + (p.budget?.options ?? 0), 0),
    optionsLeft: set.reduce((n, p) => n + (p.remaining?.options ?? 0), 0),
  };
  const lastChange = [...ledgers.values()]
    .flatMap((l) => l.entries.map((entry) => ({ entry, pool: l.pool })))
    .sort((a, b) => b.entry.at.localeCompare(a.entry.at))[0];

  return (
    <Card
      id={POOLS_ANCHOR}
      headingRef={headingRef}
      title={`Budget pools · ${String(fiscalYear)}`}
      helpAnchor="pools-table"
      help={{
        shows:
          'Each pool’s cash and options budget for the year, what’s been awarded (paid and in progress) and what’s left.',
        canDo:
          'Executives, Finance, Stock Admins and System Admins set or change a budget here. Every change asks for a reason and lands in the budget ledger below.',
      }}
      aside={
        <div className={s.asideRow}>
          <WhoEdits canEdit={canAdjust}>Executives, Finance and Stock Admins edit</WhoEdits>
          <Select
            label="Year"
            options={years.map((y) => ({ id: String(y), label: String(y) }))}
            value={String(fiscalYear)}
            onChange={(id) => {
              if (id !== null) onYear(Number(id));
            }}
          />
        </div>
      }
    >
      <ChangedLine
        change={
          lastChange === undefined
            ? null
            : {
                by: lastChange.entry.by,
                at: lastChange.entry.at,
                // Only a change done for someone says which pool and kind (Budget board); every
                // other line reads as it always has.
                ...(lastChange.entry.for != null && {
                  for: lastChange.entry.for,
                  what: `${lastChange.pool.name} ${LEDGER_KIND_WORD[lastChange.entry.kind]}`,
                }),
              }
        }
        {...(needSetup && { empty: `Not set for ${year} yet` })}
        onHistory={onLedger}
        historyLabel="Budget ledger"
      />
      {needSetup && (
        // X4's day one: what to do first, and one way to start. There's no single form for every
        // pool, so the way in is the first pool's own editor.
        <div className={s.setupBanner}>
          <EmptyArt name="pool" size="sm" />
          <div className={s.setupBannerWords}>
            <h3 className={s.setupBannerTitle}>
              {canAdjust
                ? `Set all budget pools for ${year}`
                : `The ${year} budget pools aren’t set yet`}
            </h3>
            <p className={s.body}>
              {canAdjust
                ? 'Set each pool’s cash and options budget from its row below, then review and confirm. Each starting budget goes into the ledger as Initial budget. Awards can be entered once a sector’s pool is set.'
                : 'Executives, Finance and Stock Admins set each pool’s cash and options budget here. Each starting budget goes into the ledger as Initial budget, and awards can be entered once a sector’s pool is set.'}
            </p>
          </div>
          {canAdjust && firstUnset !== undefined && (
            <Button
              variant="navy"
              size="sm"
              onPress={() => {
                setFromBanner(firstUnset.pool.id);
                setEditing(firstUnset.pool.id);
              }}
            >
              {`Set the ${firstUnset.pool.name} budget`}
            </Button>
          )}
        </div>
      )}
      {pools.length === 0 ? (
        <EmptyState
          variant="not-set"
          art="pool"
          headline="No budget pools to show"
          body="Pools appear here once they’re set up. Each one has a cash and an options budget for the year."
        />
      ) : (
        <ScrollBox label="Budget pools">
          <table className={cx(s.table, s.poolsTable)}>
            <caption className="visually-hidden">
              Budget pools for {fiscalYear}: budget, adjustments, awarded and what’s left
            </caption>
            <thead>
              <tr>
                <th scope="col">Pool</th>
                <th scope="col" className={s.num}>
                  Cash budget
                </th>
                <th scope="col" className={s.num}>
                  Adjustments
                </th>
                <th scope="col" className={s.num}>
                  Awarded
                </th>
                <th scope="col" className={s.num}>
                  Cash left
                </th>
                <th scope="col" className={s.num}>
                  Options budget
                </th>
                <th scope="col" className={s.num}>
                  Options left
                </th>
                <th scope="col">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {pools.map((p) => {
                const ledger = ledgers.get(p.pool.id);
                const net = ledger === undefined ? null : ledgerSummary(ledger).netCents;
                const awarded = awardedCash(p);
                return [
                  <tr key={p.pool.id} className={cx(editing === p.pool.id && s.rowOpen)}>
                    <th scope="row" className={s.rowHead}>
                      {p.pool.name}
                      {p.kind === 'spot' && (
                        <span className={s.sub}>
                          Pays every Spot Award · cash only · never held for budget
                        </span>
                      )}
                    </th>
                    <td className={s.num}>
                      {p.budget === null ? (
                        <StatusPill tone="waiting" size="sm" label="Budget not set" />
                      ) : (
                        formatMoney(p.budget.cashCents)
                      )}
                    </td>
                    <td
                      className={cx(
                        s.num,
                        net !== null && net > 0 && s.up,
                        net !== null && net < 0 && s.down,
                      )}
                    >
                      {p.budget === null ? 'Not set' : net === null ? '…' : signedMoney(net)}
                    </td>
                    <td className={s.num}>
                      <span className={s.strong}>{formatMoney(awarded)}</span>
                      <span className={s.sub}>
                        {awarded === 0
                          ? 'Nothing awarded yet'
                          : `${formatMoney(p.paid.cashCents)} paid · ${formatMoney(p.inProgress.cashCents)} in progress`}
                      </span>
                    </td>
                    <td className={cx(s.num, s.strong)}>
                      {spotOverNoBudget(p) ? (
                        <OverBudget amount={formatMoney(awardedCash(p))} />
                      ) : p.remaining === null ? (
                        'Not set'
                      ) : p.remaining.cashCents < 0 ? (
                        <OverBudget amount={formatMoney(-p.remaining.cashCents)} />
                      ) : (
                        formatMoney(p.remaining.cashCents)
                      )}
                    </td>
                    <td className={s.num}>
                      {p.kind === 'spot'
                        ? 'Cash only'
                        : p.budget === null
                          ? 'Not set'
                          : formatCount(p.budget.options)}
                    </td>
                    <td className={s.num}>
                      {p.kind === 'spot' ? (
                        'Cash only'
                      ) : p.remaining === null ? (
                        <>
                          Not set
                          {awardedOptions(p) > 0 && (
                            <span className={s.sub}>{formatCount(awardedOptions(p))} granted</span>
                          )}
                        </>
                      ) : p.remaining.options < 0 ? (
                        <OverBudget amount={plural(-p.remaining.options, 'option', 'options')} />
                      ) : (
                        formatCount(p.remaining.options)
                      )}
                    </td>
                    <td className={s.actionsCell}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onPress={() => {
                          setHistory(p.pool.id);
                        }}
                        aria-label={`History of ${p.pool.name}`}
                      >
                        History
                      </Button>
                      {/* Last in the row, so the editor's fields come straight after it (APP-046). */}
                      {canAdjust && (
                        <Button
                          ref={(node) => {
                            if (node === null) editButtons.current.delete(p.pool.id);
                            else editButtons.current.set(p.pool.id, node);
                          }}
                          variant={p.budget === null ? 'navy' : 'ghost'}
                          size="sm"
                          aria-expanded={editing === p.pool.id}
                          aria-label={`${p.budget === null ? 'Set budget for' : 'Edit'} ${p.pool.name}`}
                          onPress={() => {
                            setFromBanner(null);
                            setEditing(editing === p.pool.id ? null : p.pool.id);
                          }}
                        >
                          {p.budget === null ? 'Set budget' : 'Edit'}
                        </Button>
                      )}
                    </td>
                  </tr>,
                  editing === p.pool.id ? (
                    <tr key={`${p.pool.id}-edit`} className={s.editRow}>
                      <td colSpan={8}>
                        <PoolEditor
                          pool={p}
                          fiscalYear={fiscalYear}
                          focusOnOpen={fromBanner === p.pool.id}
                          onDone={() => {
                            setEditing(null);
                            setFromBanner(null);
                            setReturnTo({ poolId: p.pool.id });
                          }}
                        />
                      </td>
                    </tr>
                  ) : null,
                ];
              })}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row">Total · {plural(set.length, 'set pool', 'set pools')}</th>
                <td className={s.num}>{formatMoney(total.cash)}</td>
                <td className={s.num}>{signedMoney(total.adjustments)}</td>
                <td className={s.num}>{formatMoney(total.awarded)}</td>
                <td className={s.num}>
                  {total.left < 0 ? (
                    <OverBudget amount={formatMoney(-total.left)} />
                  ) : (
                    formatMoney(total.left)
                  )}
                </td>
                <td className={s.num}>{formatCount(total.options)}</td>
                <td className={s.num}>
                  {total.optionsLeft < 0 ? (
                    <OverBudget amount={plural(-total.optionsLeft, 'option', 'options')} />
                  ) : (
                    formatCount(total.optionsLeft)
                  )}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </ScrollBox>
      )}
      <p className={s.footnote}>
        Cash budget = initial budget + additions − reductions ± moves and top-ups. Awarded = paid +
        in progress. Cash left = budget − awarded. The PAA/TAA pool pays all of every PAA and the
        PAA/TAA share of each TAA. The Spot Awards pool pays every Spot Award, in cash; a Spot Award
        is never held for its budget, so this pool can go over.
      </p>
      <PoolHistoryDrawer
        ledger={history === null ? undefined : ledgers.get(history)}
        isOpen={history !== null}
        onOpenChange={(open) => {
          if (!open) setHistory(null);
        }}
      />
    </Card>
  );
}

function MoveCard({
  pools,
  fiscalYear,
  lastMove,
  spotOnly = false,
}: {
  pools: readonly Pool[];
  fiscalYear: number;
  lastMove: string | null;
  /**
   * APP-264: Finance moves budget only to and from the Spot Awards pool; between two other pools,
   * moves are the Executives', the Stock Admin team's and System Admins'.
   */
  spotOnly?: boolean;
}): ReactElement {
  const refresh = useBudgetRefresh();
  const toast = useToast();
  const withBudget = pools.filter((p) => p.budget !== null);
  // APP-148 D20: a Stock Admin can mark the move "For an Executive".
  const actFor = useBudgetActFor();
  const [who, setWho] = useState<ForWhomValue>('me');
  const acting = actFor !== null && who === 'someone';
  const [from, setFrom] = useState<string | null>(null);
  const [chosenTo, setTo] = useState<string | null>(null);
  const [cash, setCash] = useState<number | null>(null);
  const [options, setOptions] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const options_ = withBudget.map((p) => ({ id: p.pool.id, label: p.pool.name }));
  const spotIds = new Set(withBudget.filter((p) => p.kind === 'spot').map((p) => p.pool.id));
  // Finance: one end is the Spot Awards pool, so with another pool chosen first, the other end is it.
  const toOptions = options_.filter(
    (o) => o.id !== from && (!spotOnly || from === null || spotIds.has(from) || spotIds.has(o.id)),
  );
  // A To that the From no longer allows is cleared rather than kept out of sight.
  const to = toOptions.some((o) => o.id === chosenTo) ? chosenTo : null;
  const fromPool = withBudget.find((p) => p.pool.id === from);
  const toPool = withBudget.find((p) => p.pool.id === to);
  // APP-264: the Spot Awards pool is cash only, so a move to or from it moves cash.
  const cashOnly = fromPool?.kind === 'spot' || toPool?.kind === 'spot';
  const amount = { cashCents: cash ?? 0, options: cashOnly ? 0 : (options ?? 0) };
  const valid =
    fromPool !== undefined &&
    toPool !== undefined &&
    from !== to &&
    (amount.cashCents > 0 || amount.options > 0);
  const tooMuch =
    fromPool?.remaining != null &&
    (amount.cashCents > fromPool.remaining.cashCents ||
      amount.options > fromPool.remaining.options);
  const amountText = [
    amount.cashCents > 0 ? formatMoney(amount.cashCents) : null,
    amount.options > 0 ? `${formatCount(amount.options)} options` : null,
  ]
    .filter((x): x is string => x !== null)
    .join(' and ');

  return (
    <Card
      title="Move budget between pools"
      help={{
        shows: 'Moves cash or options from one pool’s budget to another for the year.',
        canDo:
          'Executives and Stock Admins move budget between any two pools, and Finance to and from the Spot Awards pool. The move is one entry on each pool’s ledger, with your reason.',
      }}
      aside={
        <WhoEdits canEdit>
          {spotOnly
            ? 'Finance: to and from the Spot Awards pool'
            : 'Executives and Stock Admins; Finance for Spot'}
        </WhoEdits>
      }
    >
      {withBudget.length === 0 ? (
        <EmptyState
          variant="not-set"
          art="pool"
          size="sm"
          headline="Set the pools first"
          body={`Once the ${String(fiscalYear)} budgets are in, you can move budget from one pool to another here. Each move is confirmed and recorded on both pools.`}
        />
      ) : withBudget.length < 2 ? (
        <EmptyState
          variant="not-set"
          art="pool"
          size="sm"
          headline="Two pools need a budget first"
          body="A move takes from one pool’s budget and adds to another’s, so both need a budget for the year."
        />
      ) : (
        <form
          className={s.stack}
          onSubmit={(e) => {
            e.preventDefault();
            if (valid && !tooMuch && !acting) setConfirming(true);
          }}
        >
          <div className={s.fieldRow}>
            <Select label="From" options={options_} value={from} onChange={setFrom} isRequired />
            <Select label="To" options={toOptions} value={to} onChange={setTo} isRequired />
          </div>
          <div className={s.fieldRow}>
            <MoneyField label="Cash ($)" valueCents={cash} onChangeCents={setCash} />
            {!cashOnly && (
              <NumberInput label="Options" value={options} onChange={setOptions} minValue={0} />
            )}
          </div>
          {cashOnly && (
            <p className={s.muted}>The Spot Awards pool is cash only, so this move is cash.</p>
          )}
          {tooMuch && fromPool.remaining !== null && (
            <p className={s.warning} role="alert">
              {fromPool.pool.name} has {formatMoney(fromPool.remaining.cashCents)} and{' '}
              {formatCount(fromPool.remaining.options)} options left this year.
            </p>
          )}
          {actFor !== null && <BudgetForWhom value={who} onChange={setWho} />}
          {acting ? (
            <BudgetStep
              offer={actFor.offer}
              actorName={actFor.actorName}
              subtitle={`Move budget between pools · ${String(fiscalYear)}`}
              change={moveChange(
                fromPool === undefined
                  ? undefined
                  : { name: fromPool.pool.name, budget: fromPool.budget },
                toPool === undefined
                  ? undefined
                  : { name: toPool.pool.name, budget: toPool.budget },
                fiscalYear,
                amount,
              )}
              ready={valid && !tooMuch}
              onCancel={() => {
                setWho('me');
              }}
              onConfirm={async (reason, actingFor, done) => {
                if (from === null || to === null) return;
                try {
                  await moveBudget({
                    fromPoolId: from,
                    toPoolId: to,
                    fiscalYear,
                    reason,
                    ...amount,
                    actingFor,
                  });
                } finally {
                  await refresh();
                }
                toast.show({
                  tone: 'done',
                  title: `Budget moved for ${done.person.name}`,
                  body: 'It’s on both pools’ ledgers.',
                });
                setCash(null);
                setOptions(null);
                setWho('me');
              }}
            />
          ) : (
            <div className={s.formActions}>
              <Button type="submit" variant="ghost" isDisabled={!valid || tooMuch}>
                Review move
              </Button>
            </div>
          )}
        </form>
      )}
      {/* One message for an empty card: the line only goes with the form (APP-093). */}
      {withBudget.length >= 2 && <p className={s.muted}>{lastMove ?? 'No moves this year.'}</p>}
      <ConfirmDialog
        isOpen={confirming}
        onOpenChange={(open) => {
          setConfirming(open);
          if (!open) setError(null);
        }}
        title="Move this budget?"
        intro="It’s recorded on both pools’ ledgers with your name and reason."
        summary={[
          ...(fromPool?.budget != null
            ? [
                {
                  label: `${fromPool.pool.name} · cash budget`,
                  before: formatMoney(fromPool.budget.cashCents),
                  value: formatMoney(fromPool.budget.cashCents - amount.cashCents),
                },
              ]
            : []),
          ...(toPool?.budget != null
            ? [
                {
                  label: `${toPool.pool.name} · cash budget`,
                  before: formatMoney(toPool.budget.cashCents),
                  value: formatMoney(toPool.budget.cashCents + amount.cashCents),
                },
              ]
            : []),
          { label: 'Moving', value: amountText },
        ]}
        reasonLabel="Why are you moving it?"
        confirmLabel="Move budget"
        error={error}
        onConfirm={async (reason) => {
          if (!valid || from === null || to === null) return false;
          setError(null);
          try {
            await moveBudget({ fromPoolId: from, toPoolId: to, fiscalYear, reason, ...amount });
          } catch (err) {
            setError(problemText(err));
            return false;
          }
          await refresh();
          toast.show({ tone: 'done', title: 'Budget moved', body: 'It’s on both pools’ ledgers.' });
          setCash(null);
          setOptions(null);
          return true;
        }}
      />
    </Card>
  );
}

function TaaSplitEditCard(): ReactElement {
  const state = useSetting('taa.defaultSplit');
  const { save, error, clearError } = useSettingWrite();
  const saveFor = useSaveFor();
  const [editing, setEditing] = useState(false);
  const [pct, setPct] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const help = {
    shows:
      'The default share of each TAA paid from the PAA/TAA pool; the rest comes from the sector’s pool. Each person is paid wholly from one pool.',
    canDo:
      'Executives and Stock Admins change the default. On a single TAA an Executive can change the split, and Executives, Stock Admins and System Admins can move people between pools.',
  };
  if (state.status !== 'ready')
    return (
      <Card title="Default TAA funding split" help={help}>
        <SettingFallback state={state} />
      </Card>
    );
  const view = state.view;
  const split = isFraction(view.value) ? view.value : null;
  const next: Fraction | null =
    pct !== null && Number.isInteger(pct) && pct >= 0 && pct <= 100
      ? reduce({ num: pct, den: 100 })
      : null;
  const text = (v: unknown) =>
    isFraction(v)
      ? `${percentText(v)} PAA/TAA pool · ${percentText({ num: v.den - v.num, den: v.den })} sector pool`
      : 'Not set';
  return (
    <Card
      title="Default TAA funding split"
      help={help}
      aside={
        <WhoEdits canEdit={view.canEdit}>Executives and Stock Admins edit · Finance views</WhoEdits>
      }
    >
      {split !== null && (
        <TaaSplitBar split={split}>
          By person: each person is paid wholly from one pool, and the app assigns people to match.
          On a single TAA an Executive can change the split, and Executives, Stock Admins and System
          Admins can move people between pools. Every move is logged. TAAs are cash only; a PAA is
          never split.
        </TaaSplitBar>
      )}
      {view.canEdit &&
        (editing ? (
          <form
            className={s.inlineForm}
            onSubmit={(e) => {
              e.preventDefault();
              if (next !== null) setConfirming(true);
            }}
          >
            <NumberInput
              label="PAA/TAA pool share (%)"
              value={pct}
              onChange={setPct}
              minValue={0}
              maxValue={100}
              isRequired
              description="Whole percent. The sector pool pays the rest."
            />
            <div className={s.formActions}>
              <Button type="submit" variant="navy" isDisabled={next === null}>
                Review change
              </Button>
              <Button
                variant="ghost"
                onPress={() => {
                  setEditing(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <div className={s.formActions}>
            <Button
              variant="ghost"
              size="sm"
              onPress={() => {
                setPct(split === null ? 50 : Math.round((split.num / split.den) * 100));
                setEditing(true);
              }}
            >
              Edit split
            </Button>
          </div>
        ))}
      <ChangedLine
        change={view.lastChanged}
        onHistory={() => {
          setHistoryOpen(true);
        }}
      />
      <SettingConfirm
        subject="Default TAA funding split"
        isOpen={confirming}
        onOpenChange={(open) => {
          setConfirming(open);
          if (!open) clearError();
        }}
        title="Change the default TAA split?"
        intro="New TAAs use it. TAAs already entered keep the people and pools they were given."
        summary={[
          {
            label: 'Default TAA split',
            before: text(view.value),
            value: next === null ? '' : text(next),
          },
        ]}
        reasonRequired={view.reasonRequired}
        reasonLabel={view.reasonRequired ? 'Reason' : 'Reason (optional)'}
        confirmLabel="Change split"
        error={error}
        onConfirmFor={async (reason, actingFor, forName) => {
          if (next === null) return;
          await saveFor(
            {
              key: 'taa.defaultSplit',
              scope: 'system',
              value: next,
              ...(reason !== '' && { reason }),
              actingFor,
            },
            'Split changed',
            forName,
          );
          setEditing(false);
        }}
        onConfirm={async (reason) => {
          if (next === null) return false;
          const ok = await save(
            {
              key: 'taa.defaultSplit',
              scope: 'system',
              value: next,
              ...(reason !== '' && { reason }),
            },
            'Split changed',
          );
          if (ok) setEditing(false);
          return ok;
        }}
      />
      <SettingHistoryDrawer
        isOpen={historyOpen}
        onOpenChange={setHistoryOpen}
        settingKey="taa.defaultSplit"
        title="Default TAA funding split"
        subject="History · Budgets"
        format={text}
        numeric={(v) => (isFraction(v) ? Math.round((v.num / v.den) * 1000) / 10 : null)}
        formatNumber={(v) => `${String(v)}% PAA/TAA`}
      />
    </Card>
  );
}

/** Budget pools (X4): edit in place with a confirm summary; every change lands in the ledger. */
export function BudgetPoolsPage({ section }: { section: Section }): ReactElement {
  const { data: me } = useSuspenseQuery(meQuery);
  const current = useQuery(budgetsQuery);
  const [chosen, setChosen] = useState<number | null>(null);
  const location = useLocation();
  const poolsHeading = useRef<HTMLHeadingElement>(null);
  const ledgerHeading = useRef<HTMLHeadingElement>(null);
  const fiscalYear =
    chosen ??
    current.data?.fiscalYear ??
    me.organization.fiscalYear?.current ??
    fiscalYearOn(organizationToday(new Date(), me.organization.timeZone));
  const positions = useQuery(budgetsForYearQuery(fiscalYear));
  const pools = positions.data?.pools ?? [];
  const ledgerQueries = useQueries({
    queries: pools.map((p) => poolLedgerQuery(p.pool.id, fiscalYear)),
  });
  const ledgers = ledgerQueries.flatMap((q) => (q.data === undefined ? [] : [q.data]));
  const ledgerMap = new Map(ledgers.map((l) => [l.pool.id, l]));
  const loaded = positions.data !== undefined;
  // Budgets & history's "Open the ledger" (APP-045 A59), and the hero's and the empty ledger's
  // links to the pools, land on the card's heading once the pools and their ledgers have loaded
  // and stopped moving it down the page. Once per navigation, so a change of year doesn't jump
  // again; a same-page link is a new navigation (a new key), as it keeps the scroll where it was.
  const settled = !positions.isPending && !ledgerQueries.some((q) => q.isPending);
  const landed = useRef<string | null>(null);
  useEffect(() => {
    if (!settled || landed.current === location.key) return;
    landed.current = location.key;
    if (location.hash === `#${POOLS_ANCHOR}`) jumpTo(poolsHeading.current);
    else if (location.hash === `#${LEDGER_ANCHOR}`) jumpTo(ledgerHeading.current);
  }, [location.hash, location.key, settled]);

  // Executive › Budget pools: the Executives', and System Admins see every page (APP-134).
  const allowed =
    section === 'executive' ? opensPage(me, ['executive']) : canOpenArea(me, 'budgets');
  if (!allowed) return <NotYourArea area={section === 'executive' ? 'executive' : 'budgets'} />;
  const readOnly = me.preview !== null;
  const canAdjust = !readOnly && holds(me, 'executive', 'finance', 'stock_admin', 'system_admin');
  const movesAny = holds(me, 'executive', 'stock_admin', 'system_admin');
  // APP-264: Finance moves budget to and from the Spot Awards pool.
  const movesSpot = holds(me, 'finance') && pools.some((p) => p.kind === 'spot');
  const canMove = !readOnly && (movesAny || movesSpot);
  const baseYear = current.data?.fiscalYear ?? fiscalYear;
  const years = [baseYear - 1, baseYear, baseYear + 1];

  const set = pools.filter((p) => p.remaining !== null);
  const left = set.reduce((n, p) => n + (p.remaining?.cashCents ?? 0), 0);
  const budget = set.reduce((n, p) => n + (p.budget?.cashCents ?? 0), 0);
  const optionsLeft = set.reduce((n, p) => n + (p.remaining?.options ?? 0), 0);
  const optionsBudget = set.reduce((n, p) => n + (p.budget?.options ?? 0), 0);
  const optionsGranted = set.reduce((n, p) => n + awardedOptions(p), 0);
  const awarded = pools.reduce((n, p) => n + awardedCash(p), 0);
  const nothingAwarded = awarded === 0 && pools.every((p) => awardedOptions(p) === 0);
  const unset = pools.filter((p) => p.budget === null);
  // APP-264: the Spot Awards pool has no options, so the options tile doesn't wait on it.
  const optionsUnset = unset.filter((p) => p.kind !== 'spot');
  const spotPool = pools.find((p) => p.kind === 'spot');
  const lastMove = mergeLedgers(ledgers).find((r) => r.entry.kind === 'move_out');
  const year = String(fiscalYear);
  // X4's day one: the pools exist and none has a budget for the year.
  const needSetup = loaded && pools.length > 0 && set.length === 0;
  /** A hero tile with nothing to count yet: the way to set it, or who sets it. */
  const notSetStat = (label: ScopedLabel, message: string, whoSets: string): HeroStat =>
    canAdjust
      ? {
          label,
          value: 'Not set',
          notSet: { message, actionLabel: 'Set the budget pools', href: `#${POOLS_ANCHOR}` },
        }
      : { label, value: 'Not set', meta: whoSets, tone: 'muted' };
  const stats: HeroStat[] =
    pools.length === 0
      ? []
      : needSetup
        ? [
            notSetStat(
              scoped('All pools', `cash left ${year}`),
              `Set the ${year} pools to begin`,
              `Executives and Finance set the ${year} pools`,
            ),
            notSetStat(
              scoped('All pools', `options left ${year}`),
              'Options budgets are set with the pools',
              'Options budgets are set with the pools',
            ),
          ]
        : [
            {
              label: scoped('All pools', `cash left ${year}`),
              // Over budget in words, never a minus sign alone (QA-036).
              value: left < 0 ? `${formatMoney(-left)} over budget` : formatMoney(left),
              meta:
                awarded === 0
                  ? `$0 awarded · full ${formatMoney(budget)} available · ${plural(set.length, 'pool', 'pools')}`
                  : `${formatMoney(awarded)} awarded of ${formatMoney(budget)} · ${plural(set.length, 'pool', 'pools')}`,
            },
            {
              label: scoped('All pools', `options left ${year}`),
              value:
                optionsLeft < 0
                  ? `${plural(-optionsLeft, 'option', 'options')} over budget`
                  : formatCount(optionsLeft),
              meta: `${formatCount(optionsGranted)} granted of ${formatCount(optionsBudget)}${
                optionsUnset.length > 0
                  ? ` · ${listText(optionsUnset.map((p) => p.pool.name))} ${optionsUnset.length === 1 ? 'pool' : 'pools'} not set`
                  : ''
              }`,
              tone: 'gold',
            },
            // APP-264: the Spot Awards pool's own figure, scoped to it.
            ...(spotPool === undefined
              ? []
              : [
                  spotOverNoBudget(spotPool)
                    ? {
                        label: scoped(spotPool.pool.name, `cash left ${year}`),
                        value: `${formatMoney(awardedCash(spotPool))} over budget`,
                        meta: 'No budget set · Spot Awards carry on, cash only',
                      }
                    : spotPool.remaining === null
                      ? notSetStat(
                          scoped(spotPool.pool.name, `cash left ${year}`),
                          `Set the ${year} ${spotPool.pool.name} budget`,
                          `Executives, Finance and Stock Admins set it`,
                        )
                      : {
                          label: scoped(spotPool.pool.name, `cash left ${year}`),
                          value:
                            spotPool.remaining.cashCents < 0
                              ? `${formatMoney(-spotPool.remaining.cashCents)} over budget`
                              : formatMoney(spotPool.remaining.cashCents),
                          meta: `${formatMoney(awardedCash(spotPool))} awarded of ${formatMoney(spotPool.budget?.cashCents ?? 0)} · every Spot Award, cash only`,
                        },
                ]),
          ];
  const sub = needSetup
    ? canAdjust
      ? `Nothing is set for ${year} yet. Set each pool’s budget below and confirm; each pool starts its ledger, and Sector Leads can enter awards once their pool is set.`
      : `Nothing is set for ${year} yet. Executives, Finance and Stock Admins set each pool’s budget here; each pool starts its ledger, and Sector Leads can enter awards once their pool is set.`
    : loaded && set.length > 0 && nothingAwarded
      ? canAdjust
        ? `Your ${year} budgets are set and nothing has been awarded yet. Adjust any pool here: you confirm each change, and the budget ledger records it.`
        : `The ${year} budgets are set and nothing has been awarded yet. Every change is confirmed and recorded in the budget ledger.`
      : `Set and adjust each pool’s ${year} cash and options budget. You confirm every change, and the budget ledger records who changed what, for year-end reporting.`;

  return (
    <SettingsFrame
      section={section}
      hero={{
        eyebrow: `${section === 'executive' ? 'Executive' : 'Settings'} · Budget pools · FY${year}`,
        title: 'Budget pools',
        sub,
        stats,
      }}
    >
      <div className={s.stack}>
        {positions.isError ? (
          <Card
            title={`Budget pools · ${String(fiscalYear)}`}
            help={{ shows: 'Each pool’s budget for the year.', canDo: 'Reload to try again.' }}
          >
            <p className={s.muted} role="alert">
              The pools didn’t load. Reload the page to try again.
            </p>
          </Card>
        ) : positions.data === undefined ? (
          <Card
            title={`Budget pools · ${String(fiscalYear)}`}
            help={{ shows: 'Each pool’s budget for the year.', canDo: 'Wait a moment.' }}
          >
            <p className={s.muted}>Loading the pools…</p>
          </Card>
        ) : (
          <PoolsCard
            pools={pools}
            ledgers={ledgerMap}
            fiscalYear={fiscalYear}
            years={years}
            onYear={setChosen}
            canAdjust={canAdjust}
            headingRef={poolsHeading}
            onLedger={() => {
              jumpTo(ledgerHeading.current);
            }}
          />
        )}
        <div className={s.pair}>
          {canMove && (
            <MoveCard
              pools={pools}
              fiscalYear={fiscalYear}
              spotOnly={!movesAny}
              lastMove={
                lastMove === undefined || lastMove.entry.otherPool === null
                  ? null
                  : `Last move: ${formatMoney(-lastMove.entry.cashCents)} from ${lastMove.pool.name} to ${lastMove.entry.otherPool.name} by ${lastMove.entry.by?.name ?? 'MyRewards setup'} on ${formatDate(lastMove.entry.at)} · recorded on both pools`
              }
            />
          )}
          <TaaSplitEditCard />
        </div>
        <AskDataEntry about="Ask how the pools are tracking in plain words: what’s left, what’s in progress against what’s paid, and where the year is heading, with a chart and the table behind it." />
        <LedgerCard
          pools={pools}
          ledgers={ledgers}
          fiscalYear={fiscalYear}
          loading={!loaded || ledgerQueries.some((q) => q.isPending)}
          previewing={readOnly}
          canSet={canAdjust && needSetup}
          headingRef={ledgerHeading}
        />
      </div>
    </SettingsFrame>
  );
}

/** Settings › Budgets (Finance and the Stock Admin team edit). */
export function SettingsBudgetPoolsPage(): ReactElement {
  return <BudgetPoolsPage section="settings" />;
}

/** Executive › Budget pools (X4). */
export function ExecutiveBudgetPoolsPage(): ReactElement {
  return <BudgetPoolsPage section="executive" />;
}
